import { ReactNode } from 'react';
import {
	ArrayField,
	DateField,
	DeleteButton,
	EmailField,
	FunctionField,
	ReferenceField,
	SingleFieldList,
	TextField,
	useNotify,
	useRecordContext,
	useTranslate,
	WithRecord
} from 'react-admin';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import {
	ContentCopy,
	Edit,
	Link as LinkIcon,
	Visibility
} from '@mui/icons-material';
import {
	Box,
	Button,
	Divider,
	IconButton,
	Stack,
	Tooltip,
	Typography
} from '@mui/material';
import {
	AddToFavoritesButton,
	ChangeObjectStatus,
	StatusChip,
	TagsList
} from '../../../components';
import { OBJECTS_TYPES } from '../../../constants';

export const ApartmentAside = ({
	link = 'edit'
}: {
	link?: 'edit' | 'show';
}) => {
	const t = useTranslate();
	const location = useLocation();
	const record = useRecordContext();
	const notify = useNotify();
	if (!record) return null;

	const externalLink = `${window.location.origin}/#/properties/show-page/${record.id}`;

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(externalLink);
			notify('Link copied to clipboard', {
				type: 'info'
			});
		} catch (err) {
			console.error('Failed to copy link: ', err);
		}
	};

	return (
		<Box ml={4} width={250} minWidth={250}>
			<Box sx={{ display: 'flex', gap: 2 }} mb={2}>
				<Button
					component={Link}
					to={`/objects/${OBJECTS_TYPES.APARTMENTS}/${link}/${record.id}`}
					variant="outlined"
					startIcon={link === 'edit' ? <Edit /> : <Visibility />}
				>
					{link === 'edit' ? t('ra.action.edit') : t('ra.action.show')}
				</Button>
				{/*<AddToFavoritesButton resource="apartments" />*/}
			</Box>
			<Typography variant="subtitle2">Презентація</Typography>
			<Divider sx={{ mt: 1, mb: 1 }} />

			<Stack direction="row" spacing={1} alignItems="center">
				Посилання: &nbsp;
				<a
					style={{
						display: 'flex',
						alignItems: 'center',
						textDecoration: 'none'
					}}
					target="_blank"
					href={externalLink}
					rel="noreferrer"
				>
					<LinkIcon color="secondary" />
				</a>
				<Tooltip title="Копіювати посилання">
					<IconButton size="small" onClick={handleCopyLink}>
						<ContentCopy />
					</IconButton>
				</Tooltip>
			</Stack>
			<Divider sx={{ mt: 1, mb: 1 }} />
			{record.status_data && (
				<Stack mb={2}>
					<Stack gap={1} direction="row" alignItems="center">
						<ChangeObjectStatus />
						<Typography variant="subtitle2">
							{t('objects.page.status')}
						</Typography>
						<StatusChip />
					</Stack>
				</Stack>
			)}
			<Typography variant="subtitle2">Tags</Typography>
			<Divider sx={{ mt: 1, mb: 1 }} />
			<TagsList resource="properties" />
			<Stack mt={2}>
				<Typography variant="subtitle2">Personal info</Typography>
				<Divider sx={{ mt: 1, mb: 1 }} />
				<Box mb={3}>
					<Typography component="span" variant="body2" color="textSecondary">
						{t('form.fields.createdAt')}
					</Typography>
					<DateField
						source="created_at"
						showTime={true}
						locales="uk-UA"
						color="textSecondary"
					/>
					<br />
					<Typography component="span" variant="body2" color="textSecondary">
						{t('form.fields.updatedAt')}
					</Typography>
					<DateField
						source="updated_at"
						showTime={true}
						locales="uk-UA"
						color="textSecondary"
					/>
					<br />
					<Typography component="span" variant="body2" color="textSecondary">
						{t('form.fields.followedBy')}
					</Typography>
					<ReferenceField source="sales_id" reference="sales">
						<FunctionField
							source="last_name"
							render={record => `${record.first_name} ${record.last_name}`}
						/>
					</ReferenceField>
				</Box>
			</Stack>

			<DeleteButton
				sx={{ mt: 3 }}
				redirect={location.state?.from || undefined}
			/>
		</Box>
	);
};

const PersonalInfoRow = ({
	icon,
	primary,
	showType
}: {
	icon: ReactNode;
	primary: ReactNode;
	showType?: boolean;
}) => (
	<Stack direction="row" alignItems="center" gap={1} minHeight={24}>
		{icon}
		<Box display="flex" flexWrap="wrap" columnGap={0.5} rowGap={0}>
			{primary}
			{showType ? (
				<WithRecord
					render={row =>
						row.type !== 'Other' && (
							<TextField source="type" color="textSecondary" />
						)
					}
				/>
			) : null}
		</Box>
	</Stack>
);
